import { useCallback, useLayoutEffect, useMemo } from 'react'

import { useLocation } from 'react-router-dom'

import { mainPaths, paths } from '@/constants/paths'
import { popupStatus } from '@/constants/popup'
import { defaultPalette } from '@/constants/themes'
import { useEventChannel } from '@/hooks/useEventChannel'
import { useFindNearestByImgWidth } from '@/hooks/useFindNearestByImgWidth'
import useStore from '@/store/useStore'
import { ImageSourceType, PaletteType } from '@/types/servicesTypes/configTypes'
import { paletteClone, updateObject } from '@/utils/utils'

export const useSetPalette = () => {
  const updateAppPalette = useCallback(
    ({ palette }: { palette?: Partial<PaletteType> }) => {
      const state = useStore.getState()
      const { setAppPalette, setCustomPalette } = state.common

      const updatedPalette = palette
        ? (updateObject(
            paletteClone(defaultPalette) as PaletteType,
            palette,
          ) as PaletteType)
        : defaultPalette
      setAppPalette(updatedPalette)
      setCustomPalette(updatedPalette)
    },
    [],
  )

  return { updateAppPalette }
}

export const useCustomPalette = () => {
  const { pathname } = useLocation()

  const { page: pageData } = useStore((state) => state.page)
  const { pagePopup: pagePopupData, pagePopupStatus } = useStore(
    (state) => state.popup,
  )
  const isPagePopupOpen = pagePopupStatus !== popupStatus.CLOSED
  const page = isPagePopupOpen ? pagePopupData : pageData

  const currentEvent = useStore((state) => state.common.currentEvent)
  const eventChannel = useEventChannel(currentEvent)

  const { calendar: calendarData, calendarIndex } = useStore(
    (state) => state.common,
  )
  const isCalendarPage = pathname.includes(paths.CALENDAR)
  const calendar = isCalendarPage && calendarData[calendarIndex]

  const isPageWithAppPallete = !mainPaths.some((path) =>
    pathname.includes(path),
  )

  const { activePalette, backgroundImageSources } = useMemo(() => {
    let activePalette: PaletteType = {}
    let backgroundImageSources: ImageSourceType[] | undefined

    if (isPageWithAppPallete) {
      return { activePalette: {}, backgroundImageSources: undefined }
    }
    if (eventChannel?.palette || eventChannel?.backgroundImage) {
      activePalette = eventChannel?.palette || {}
      backgroundImageSources = eventChannel?.backgroundImage?.sources
    } else if (page?.palette || page?.backgroundImage) {
      activePalette = page?.palette || {}
      backgroundImageSources = page?.backgroundImage?.sources
    } else if (calendar?.palette) {
      activePalette = calendar.palette
    }

    return { activePalette, backgroundImageSources }
  }, [page, currentEvent, eventChannel, calendar, isPageWithAppPallete])

  const pageImgSrc = useFindNearestByImgWidth(backgroundImageSources, 1920)?.url

  useLayoutEffect(() => {
    activePalette && updateCustomPalette({ palette: activePalette })
  }, [activePalette])

  const updateCustomPalette = useCallback(
    ({ palette }: { palette?: Partial<PaletteType> }) => {
      const state = useStore.getState()
      const { appPalette, setCustomPalette } = state.common

      const updatedPalette = palette
        ? (updateObject(
            paletteClone(appPalette) as PaletteType,
            palette,
          ) as PaletteType)
        : appPalette
      setCustomPalette(updatedPalette)
    },
    [],
  )

  return { pageImgSrc, updateCustomPalette }
}
